/* eslint-disable multiline-ternary */
import { ChevronRightIcon } from "@chakra-ui/icons"
import { Box, Button, Flex, Heading, Img, Text, useMediaQuery } from "@chakra-ui/react"
import { useMemo } from "react"
import { useNavigate } from "react-router"
import HomePageBannerImage from "../../assets/images/homePageBannerImage.png"
import { Paths, gridUnit, mobileBreakpoint } from "../../common/commonConstants"
import { useIsLoggedIn } from "../../components/commons/utils"
import { SEOConfigs, SEOHead } from "../../components/SEO/SEOHead"
import { Heading2Caps } from "../../components/commons/Heading2Caps"
import { InfinityWrapper } from "../../components/commons/inifinity-wrapper"
import { ProductThumbnail } from "../../components/commons/product"
import { Colors } from "../../theme/colors"

import { SimpleGridWrapper, SubPage, SubPageContent } from "../../components/SubPage"
import { FloorwasteAndShowerImg, StackFamilyImg, AccessoriesImg, UtilityFamilyImg, MultiServiceFamilyImg, RetrofitFamilyImg, GasFamilyImg } from "../../components/images"
import { useGetProductMenuQuery } from "../../type-and-hooks"
import { CreateNewFMP } from "../fmp/createNewFMPButton.main"

interface ProductCategory {
  label: string
  onclickLink: string
  imagePath: string | null
  heroImageUrl?: string | null
}

const getProductCategoryPageLink = (label: string): string => {
  const labelLower = label.toLowerCase()

  if (labelLower.includes("floorwaste") || labelLower.includes("floor waste")) {
    return "/products/cast-in-fire-collars"
  } else if (labelLower.includes("stack")) {
    return Paths.Product.sub.stack.all
  } else if (labelLower === "retrofit") {
    return "/products/retrofit-fire-collars"
  } else if (labelLower === "gas") {
    return "/products/gas-fire-collars"
  } else if (labelLower === "utility collar") {
    return "/products/endless-fire-collars"
  } else if (labelLower.includes("multi-service")) {
    return "/products/multi-service-fire-collars"
  }
  return ""
}

// Image mapping for product categories
const getProductCategoryImage = (label: string): string | null => {
  const labelLower = label?.toLowerCase() ?? ""

  if (labelLower.includes("floorwaste") || labelLower.includes("floor waste") || labelLower.includes("cast-in") || labelLower.includes("cast in")) {
    return FloorwasteAndShowerImg
  } else if (labelLower.includes("stack")) {
    return StackFamilyImg
  } else if (labelLower.includes("retrofit")) {
    return RetrofitFamilyImg
  } else if (labelLower.includes("gas")) {
    return GasFamilyImg
  } else if (labelLower.includes("electrical") || labelLower.includes("data")) {
    return null
  } else if (labelLower.includes("hvac") || labelLower.includes("multi-service")) {
    return MultiServiceFamilyImg
  } else if (labelLower.includes("accessories")) {
    return AccessoriesImg
  } else if (labelLower.includes("utility collar")) {
    return UtilityFamilyImg
  }

  return null // Default fallback image
}

export const HomePage = (): JSX.Element => {
  const navigate = useNavigate()
  const navigateTo = (link: string): void => {
    navigate(link)
  }

  // Homepage structured data
  const homepageStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Snap Fire Systems",
    url: "https://snapfiresystems.com",
    description: "Leading provider of fire protection solutions and fire management systems",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://snapfiresystems.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  }

  const [isDesktop] = useMediaQuery(`(min-width: ${mobileBreakpoint}px)`)
  const isLoggedIn = useIsLoggedIn()

  // Fetch product menu data
  const { data: productMenuData } = useGetProductMenuQuery({
    variables: {
      category: 0
    },
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first"
  })

  // Process product menu data into categories for thumbnails
  const productCategories = useMemo<ProductCategory[]>(() => {
    if (!productMenuData?.productMenu) {
      return []
    }

    // Extract subMenu items only from "Fire Collar" category
    const categories: ProductCategory[] = []

    // Find the "Fire Collar" menu item
    const fireCollarItem = productMenuData.productMenu.find((item: any) => item?.label === "Fire Collar")

    // Extract subMenu items from Fire Collar
    if (fireCollarItem?.subMenu && Array.isArray(fireCollarItem.subMenu)) {
      fireCollarItem.subMenu.forEach((subItem: any) => {
        if (subItem?.label && subItem.onclickLink) {
          categories.push({
            label: subItem.label,
            onclickLink: getProductCategoryPageLink(subItem.label),
            heroImageUrl: subItem.heroImageUrl ?? null,
            imagePath: subItem.heroImageUrl || getProductCategoryImage(subItem.label)
          })
        }
      })
    }

    return categories
  }, [productMenuData])

  return (
    <>
      <SEOHead {...SEOConfigs.home} structuredData={homepageStructuredData} canonicalUrl="https://snapfiresystems.com/" />
      <SubPage>
        {/* Banner */}
        <InfinityWrapper inputStyles={{ height: "540px" }}>
          <Box w="100%" bg={[Colors.Secondary.banner]}>
            <Flex
              w="100%"
              minHeight={["320px", "320px", "320px", "445px", "445px", "445px"]}
              maxW={["100%", "100%", "100%", "100%"]}
              flexDir={["column-reverse", "column-reverse", "row"]}
              justify={["center"]}
              align={["center"]}
              margin="auto"
              px={[4, 4, 4, 0, 0, 0]}
              bg={[Colors.SecondaryPrimary.banner, Colors.SecondaryPrimary.banner, "transparent"]}
              mt="40"
            >
              <Box
                w={["100%", "100%", "100%", "270px", "270px", "270px"]}
                mr={[0, 0, 0, "14px", "14px", "14px"]}
                px={[4, 4, 4, 0, 0, 0]}
                mt={[1, 1, 1, 0, 0, 0]}
              >
                {isDesktop && (
                  <Heading variant="Heading2SemiBold" color="primary" m="0">
                    Easy To Install.
                    {isDesktop && (
                      <>
                        <br />
                      </>
                    )}
                    Versatile. <br />
                    Cost-Effective.
                  </Heading>
                )}
                {!isDesktop && (
                  <Heading variant="Heading2SemiBold" color="primary" m="0" p={[2, 2, 2, 0]} pb="0" mb="0">
                    <Box mb="0px" pt={["6px", "2px", "0px"]}>
                      Easy to Install. Versatile.{" "}
                    </Box>
                    Cost-Effective.
                  </Heading>
                )}
                <Heading
                  pl="0"
                  pt={[2, 2, 2, 0, 0, 0]}
                  fontSize={["15px", "15px", "19px"]}
                  variant="Heading5Italic"
                  mt={["0", "0", "0"]}
                  // height={isDesktop ? '55px' : 'auto'}
                  lineHeight={isDesktop ? "23px" : "16px"}
                  display={"flex"}
                  alignItems="center"
                  px={[2, 2, 2, 0, 0, 0]}
                >
                  The only smoke and fire collar
                </Heading>
              </Box>
              <Img
                src={HomePageBannerImage}
                maxHeight={["320px", "320px", "320px", "445px", "445px", "445px"]}
                maxW="478px"
                alt="Snap Fire Systems - Fire Protection Solutions Banner"
              />
            </Flex>

            <Flex
              columnGap={"8px"}
              pt={[2, 2, 2, 0]}
              px={[7, 7, 0]}
              justify={["center", "center", "center", "space-between"]}
              w={["100%", "100%", "660px"]}
              margin="auto"
              mt={["4", "4", "0px"]}
              mb="0"
            >
              <Button
                variant="Button1RegularOutline"
                px={["18px"]}
                fontSize={["12px", "16px", "16px", "16px", "16px"]}
                fontWeight="400"
                borderColor="primary"
                borderWidth="1px"
                maxW="185px"
                onClick={() => {
                  navigate(Paths.Product.all)
                }}
              >
                See Product Range
              </Button>
              <Button
                variant="Button1RegularWithShadow"
                px={["18px"]}
                fontSize={["12px", "16px", "16px", "16px", "16px"]}
                fontWeight="400"
                maxW={["109px", "109px", "109px", "179px"]}
                onClick={() => {
                  navigate(isLoggedIn ? Paths.FMP.PRODUCT_FILTER : Paths.PRODUCT_FILTER)
                }}
              >
                {isDesktop ? "Use Product Filter" : "See More"}
              </Button>
              {isDesktop && (
                <CreateNewFMP
                  buttonText="Create A Fire Management Plan"
                  buttonProps={{ variant: "Button1RegularAccentWithShadow", px: "18px", pt: "6px" }}
                />
              )}
            </Flex>
          </Box>
        </InfinityWrapper>
        <SubPageContent>
          {/* Why choose snapCollars */}
          <Flex w="100%" justify={"center"} flexDir={"column"} textAlign={"center"}>
            <Heading variant="Heading3PT" fontSize={["24px", "24px", "22.5px"]} mb={["16px", "16px", "12px"]} mt="0" color={"blackText"}>
              Why Choose Snap Fire Systems?
            </Heading>
            <Text variant="Body1Regular" color={"blackText"} px={[4, 0, 0]}>
              We work hand in hand with industry to make passive fire solutions easy and absolutely effective.
            </Text>
          </Flex>
          {/* Browse products below */}
          <Flex mt={["30px", "30px", "24px"]} mb="16px" align="center">
            <Heading2Caps text="Browse our products Below" />
          </Flex>
          <Box mx={["4", "4", "auto"]}>
            {/* products list - now dynamic from API */}
            <SimpleGridWrapper>
              {productCategories.length > 0 ? (
                productCategories.map((category, index) => (
                  <ProductThumbnail
                    key={`product-${index}`}
                    gridUnit={gridUnit}
                    name={category.label}
                    imagePath={category.imagePath ?? undefined}
                    redirectURL={category.onclickLink}
                  />
                ))
              ) : (
                // Fallback to hardcoded products if API data is not available
                <>
                  {["Stack", "Floorwaste & Shower", "Retrofit", "Gas", "Utility Collar", "Multi-Service"].map((label, index) => (
                    <ProductThumbnail
                      key={`fallback-${index}`}
                      gridUnit={gridUnit}
                      name={label}
                      imagePath={getProductCategoryImage(label) ?? undefined}
                      redirectURL={getProductCategoryPageLink(label)}
                    />
                  ))}
                </>
              )}
            </SimpleGridWrapper>
            <Flex justify={"flex-end"} mt={["28px", "20px", "20px"]}>
              <Button
                color={Colors.SecondaryPrimary.c2}
                variant="linkButtonBig"
                rightIcon={<ChevronRightIcon ml="0" pl="0" top="2px" left="-8px" pos="relative" />}
                onClick={() => {
                  navigateTo(Paths.Product.all)
                }}
              >
                See All
              </Button>
            </Flex>
          </Box>
        </SubPageContent>
        {/* <Loading loading={loading} /> */}
      </SubPage>
    </>
  )
}